/* Ayudate · Enrutador y red de seguridad
   Decide qué página mostrar según la dirección del navegador y envuelve todo
   en un componente que atrapa fallos, para que un error nunca deje la página
   en blanco. Es el único punto de entrada de la aplicación.                 */

import React, { useState, useEffect } from "react";
import { normalizar, RUTAS } from "./lib/rutas";
import { ErrorBoundary } from "./componentes/ErrorBoundary";
import { SitioPublico } from "./paginas/SitioPublico";
import { PaginaCalificar } from "./paginas/Calificar";
import { PaginaSalaJitsi } from "./paginas/SalaJitsi";
import { PaginaSubirComprobante } from "./paginas/SubirComprobante";
import { PaginaAdmin } from "./paginas/Admin";
import { PaginaLogin } from "./paginas/Login";
import { PaginaRestablecerPassword } from "./paginas/RestablecerPassword";
import { PaginaDashboardTutor } from "./paginas/DashboardTutor";
import { PaginaCrearCuentaEstudiante } from "./paginas/CrearCuentaEstudiante";
import { PaginaRestablecerPasswordEstudiante } from "./paginas/RestablecerPasswordEstudiante";
import { PaginaDashboardEstudiante } from "./paginas/DashboardEstudiante";
import { Pagina404 } from "./paginas/Pagina404";

/* ==================================================================
   ENRUTADOR

   Destinos:
     /              sitio público para estudiantes
     /tutores       mismo sitio, orientado a quien quiere dar tutorías
                    (con las credenciales del dashboard, ver FormTutor.jsx)
     /preguntas     preguntas y respuestas
     /nosotros      quiénes somos
     /calificar     se abre desde el enlace del correo, tras una sesión
     /sala          sala de videollamada embebida (Jitsi), tras el pago
     /pagar         se abre desde el botón del correo de cobro: ahí el
                    estudiante sube el comprobante de su SINPE, en vez de
                    responder el correo (ver SubirComprobante.jsx)
     /admin         panel de administración: cola de aprobación de
                    tutores y cursos pendientes. Sigue pidiendo la contraseña
                    de administración antes de mostrar nada; lo único que
                    cambió es la dirección, que antes era /ciwis
     /login         login único del panel: prueba tutor y estudiante, y
                    manda a /paneltutor o /panelestudiante según con cuál
                    coincida (ver iniciarSesionUnificado en apps-script.gs)
     /restablecer-password  "olvidé mi contraseña" del tutor, con el token
                    del correo
     /paneltutor    dashboard del tutor (protegida por sessionToken)
     /crear-cuenta-estudiante       alta directa de cuenta de estudiante:
                                    nombre/correo/teléfono + credenciales,
                                    sin token de invitación
     /restablecer-password-estudiante  "olvidé mi contraseña" del estudiante
     /panelestudiante               panel del estudiante (protegida por sessionToken)
     *              cualquier otra dirección: página 404

   Es a propósito diminuto: no vale la pena una librería para esto.
   IMPORTANTE al publicar: Cloudflare Pages necesita el archivo
   public/_redirects, o entrar directo a cualquier ruta da 404.
================================================================== */
export default function App() {
  const [ruta, setRuta] = useState(() => normalizar(window.location.pathname));
  /* De qué página se venía, para que el botón de volver de /preguntas y
     /nosotros lleve al lugar correcto. */
  const [origen, setOrigen] = useState(() => window.history.state?.desde || "/");

  useEffect(() => {
    const alCambiar = () => {
      setRuta(normalizar(window.location.pathname));
      setOrigen(window.history.state?.desde || "/");
    };
    window.addEventListener("popstate", alCambiar);
    return () => window.removeEventListener("popstate", alCambiar);
  }, []);

  const conocida = RUTAS.includes(ruta);

  useEffect(() => {
    document.title = ruta === "/calificar" ? "Califique su tutoría · Ayudate"
      : ruta === "/sala" ? "Su sala · Ayudate"
      : ruta === "/pagar" ? "Suba su comprobante · Ayudate"
      : ruta === "/admin" ? "Administración · Ayudate"
      : ruta === "/login" ? "Iniciar sesión · Ayudate"
      : ruta === "/restablecer-password" ? "Restablecer contraseña · Ayudate"
      : ruta === "/paneltutor" ? "Su dashboard · Ayudate"
      : ruta === "/crear-cuenta-estudiante" ? "Cree su cuenta · Ayudate"
      : ruta === "/restablecer-password-estudiante" ? "Restablecer contraseña · Ayudate"
      : ruta === "/panelestudiante" ? "Su panel · Ayudate"
      : ruta === "/tutores" ? "Dar tutorías · Ayudate"
      : ruta === "/preguntas" ? "Preguntas y respuestas · Ayudate"
      : ruta === "/nosotros" ? "Quiénes somos · Ayudate"
      : !conocida ? "Página no encontrada · Ayudate"
      : "Ayudate · Tutorías entre estudiantes";
    window.scrollTo({ top: 0 });
  }, [ruta, conocida]);

  const modo = { "/tutores": "tutores", "/preguntas": "preguntas", "/nosotros": "nosotros" }[ruta]
    || "estudiantes";

  return (
    <ErrorBoundary key={ruta}>
      {ruta === "/calificar" ? <PaginaCalificar />
        : ruta === "/sala" ? <PaginaSalaJitsi />
        : ruta === "/pagar" ? <PaginaSubirComprobante />
        : ruta === "/admin" ? <PaginaAdmin />
        : ruta === "/login" ? <PaginaLogin />
        : ruta === "/restablecer-password" ? <PaginaRestablecerPassword />
        : ruta === "/paneltutor" ? <PaginaDashboardTutor />
        : ruta === "/crear-cuenta-estudiante" ? <PaginaCrearCuentaEstudiante />
        : ruta === "/restablecer-password-estudiante" ? <PaginaRestablecerPasswordEstudiante />
        : ruta === "/panelestudiante" ? <PaginaDashboardEstudiante />
        : !conocida ? <Pagina404 />
        : <SitioPublico modo={modo} origen={origen} key={modo} />}
    </ErrorBoundary>
  );
}
